import { RiskFieldsTypeEnum } from '@/__generated__/graphql';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { FloatingLine, tabStyles } from '@/components/shared/RiskFactorInputTabs/styles';
import { abyssColors } from '@/theme/colors';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import OpenDialogButton from '../../shared/OpenDialogButton';
import { RiskTextField } from '../../shared/styles';

type Props = {
  onChange: ({
    pofValues,
    inputs,
  }: {
    pofValues?: { [key: string]: string };
    inputs?: {
      id: string;
      value: string;
    }[];
  }) => void;
};

const tabLabels: { [key: string]: string } = {
  risk_safety_calculated_value_pof: 'Safety',
  risk_environment_calculated_value_pof: 'Environment',
  risk_public_impact_calculated_value_pof: 'Public Impact',
  risk_direct_cost_calculated_value_pof: 'Direct Cost',
  risk_business_loss_calculated_value_pof: 'Business Loss',
};

export const BasePoF = ({ onChange }: Props) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: { P1: string; P2: string };
  }>({
    risk_safety_calculated_value_pof: { P1: '', P2: '' },
    risk_environment_calculated_value_pof: { P1: '', P2: '' },
    risk_public_impact_calculated_value_pof: { P1: '', P2: '' },
    risk_direct_cost_calculated_value_pof: { P1: '', P2: '' },
    risk_business_loss_calculated_value_pof: { P1: '', P2: '' },
  });
  const [value, setValue] = useState('risk_safety_calculated_value_pof');

  const changeP1 = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const P2 = selectedValues[value]?.P2;
      setSelectedValues((current) => ({
        ...current,
        [value]: { P1: event.target.value, P2 },
      }));
    },
    [selectedValues, value]
  );

  const changeP2 = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const P1 = selectedValues[value]?.P1;
      setSelectedValues((current) => ({
        ...current,
        [value]: { P1, P2: event.target.value },
      }));
    },
    [selectedValues, value]
  );

  useEffect(() => {
    const pValues: { [key: string]: string } = {
      risk_safety_calculated_value_pof: '',
      risk_environment_calculated_value_pof: '',
      risk_public_impact_calculated_value_pof: '',
      risk_direct_cost_calculated_value_pof: '',
      risk_business_loss_calculated_value_pof: '',
    };
    const inputs = [
      'risk_safety',
      'risk_environment',
      'risk_public_impact',
      'risk_direct_cost',
      'risk_business_loss',
    ].flatMap((category) => {
      const key = `${category}_calculated_value_pof`;
      const p1Value = selectedValues[key].P1 ?? '';
      let p2Value = selectedValues[key].P2;
      p2Value = p1Value && !p2Value ? '1' : p2Value || ''; // if p1 is set and p2 is not set, default to 1

      if (p1Value && p2Value) {
        const result = Number(p1Value) * Number(p2Value);
        pValues[key] = result.toString();
      }

      return [
        { id: `${category}_value_pof_p1`, value: p1Value },
        { id: `${category}_value_pof_p2`, value: p2Value },
      ];
    });

    onChange({ pofValues: pValues, inputs });
  }, [onChange, selectedValues]);

  const getRiskFields = useCallback(
    (key: string) => {
      return (
        <Stack direction="row" spacing={2} sx={{ m: 2 }}>
          <Stack direction="row" alignItems="flex-end" gap={3}>
            <RiskTextField
              label="P1"
              variant="standard"
              value={selectedValues[key]?.P1}
              onChange={changeP1}
              error={
                !/^(N\/A|\d*\.?\d*)$/.test(selectedValues[key]?.P1 ?? '')
              }
              helperText={
                !/^(N\/A|\d*\.?\d*)$/.test(selectedValues[key]?.P1 ?? '')
                  ? 'Only numbers are allowed'
                  : ''
              }
              data-testid={(() => {
                if (key === 'risk_safety_calculated_value_pof') return 'risk_safety_value_pof_p1';
                if (key === 'risk_environment_calculated_value_pof')
                  return 'risk_environment_value_pof_p1';
                if (key === 'risk_public_impact_calculated_value_pof')
                  return 'risk_public_impact_value_pof_p1';
                if (key === 'risk_direct_cost_calculated_value_pof')
                  return 'risk_direct_cost_value_pof_p1';
                return 'risk_business_loss_value_pof_p1';
              })()}
            />
            <OpenDialogButton
              location={RiskFieldsTypeEnum.Pof.toString()}
              title={`${tabLabels[key]} P1(POF)`}
              identifer={(() => {
                if (key === 'risk_safety_calculated_value_pof') return 'risk_safety_value_pof_p1';
                if (key === 'risk_environment_calculated_value_pof')
                  return 'risk_environment_value_pof_p1';
                if (key === 'risk_public_impact_calculated_value_pof')
                  return 'risk_public_impact_value_pof_p1';
                if (key === 'risk_direct_cost_calculated_value_pof')
                  return 'risk_direct_cost_value_pof_p1';
                return 'risk_business_loss_value_pof_p1';
              })()}
            />
          </Stack>
          <Stack direction="row" alignItems="flex-end" gap={3}>
            <RiskTextField
              label="P2"
              value={selectedValues[key]?.P2}
              onChange={changeP2}
              variant="standard"
              error={
                !/^(N\/A|\d*\.?\d*)$/.test(selectedValues[key]?.P2 ?? '')
              }
              helperText={
                !/^(N\/A|\d*\.?\d*)$/.test(selectedValues[key]?.P2 ?? '')
                  ? 'Only numbers are allowed'
                  : ''
              }
              data-testid={(() => {
                if (key === 'risk_safety_calculated_value_pof') return 'risk_safety_value_pof_p2';
                if (key === 'risk_environment_calculated_value_pof')
                  return 'risk_environment_value_pof_p2';
                if (key === 'risk_public_impact_calculated_value_pof')
                  return 'risk_public_impact_value_pof_p2';
                if (key === 'risk_direct_cost_calculated_value_pof')
                  return 'risk_direct_cost_value_pof_p2';
                return 'risk_business_loss_value_pof_p2';
              })()}
            />
            <OpenDialogButton
              location={RiskFieldsTypeEnum.Pof.toString()}
              identifer={(() => {
                if (key === 'risk_safety_calculated_value_pof') return 'risk_safety_value_pof_p2';
                if (key === 'risk_environment_calculated_value_pof')
                  return 'risk_environment_value_pof_p2';
                if (key === 'risk_public_impact_calculated_value_pof')
                  return 'risk_public_impact_value_pof_p2';
                if (key === 'risk_direct_cost_calculated_value_pof')
                  return 'risk_direct_cost_value_pof_p2';
                return 'risk_business_loss_value_pof_p2';
              })()}
              title={`${tabLabels[key]} P2(POF)`}
            />
          </Stack>
        </Stack>
      );
    },
    [changeP1, changeP2, selectedValues]
  );

  const tabs = useMemo(() => {
    return Object.keys(tabLabels).map((key, index) => ({
      label: tabLabels[key],
      component: getRiskFields(key),
      index,
      key,
    }));
  }, [getRiskFields]);

  const handleTabSwitch = (index: number) => {
    const newTab = tabs.find((tab) => tab.index === index);
    setValue(newTab?.key ?? '');
    setTabIndex(index);
  };
  return (
    <>
      <Typography fontSize="1.2rem" color={abyssColors.primary[500]}>
        Probability reduction factor based on consequences
      </Typography>
      <Box sx={{ width: '100%', border: '1px solid #DDDDDD' }}>
        <Box sx={{ padding: '20px' }}>
          <BasicTabs
            renderingStrategy="eager"
            tabs={tabs}
            selectedTab={tabIndex}
            changeTab={handleTabSwitch}
            tabsStyles={tabStyles}
            customSeperator={<FloatingLine />}
          />
        </Box>
      </Box>
    </>
  );
};
